import { type FC, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import { SSNField } from "@/shared/components/SSNField";
import { AddressInput } from "@/shared/components/ui/address-input";
import { Combobox } from "@/shared/components/ui/combobox";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { COUNTRIES, getCountryName } from "@/shared/constants/countries";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useDeceasedSSNData } from "@/shared/hooks/useDeceasedSSNData";
import {
  formatPlaceOfDeath,
  placeOfDeathKey,
  usePlaceOfDeath,
} from "@/shared/hooks/usePlaceOfDeath";
import { useSaveSSN } from "@/shared/hooks/useSaveSSN";
import { SSN_VALIDATION_MESSAGE, useSSNField } from "@/shared/hooks/useSSNField";
import { supabase } from "@/shared/lib/supabase";
import { logActivity } from "@/shared/utils/activityLogger";
import { cleanSSN, isValidSSN } from "@/shared/utils/formatters";
import { buildFullName } from "@/shared/utils/names";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { sentryUtils } from "@/shared/utils/sentry";
import { WHY_WE_ASK } from "@/shared/constants/whyWeAsk";

import { SummaryCard, SummaryRow } from "./SummaryCard";
import { YES_NO_LABELS, formatShortDate, getStateName } from "../summaryFormat";

import { fetchEstateJsonFields } from "../hooks/useEstateSummaryRecords";

import type { EstateRow } from "../hooks/useEstateSummaryRecords";
import type { SectionEditProps } from "./sectionEdit";

const COUNTRY_OPTIONS = COUNTRIES.map((country) => ({ value: country.code, label: country.name }));

type Metadata = Record<string, unknown>;

interface AboutForm {
  first_name: string;
  middle_name: string;
  last_name: string;
  nickname: string;
  ssn: string;
  date_of_birth: string;
  date_of_death: string;
  citizenship: string;
  occupation: string;
  employer: string;
  military: string;
  place_of_death: string;
}

const toForm = (estate: EstateRow, ssn: string, placeOfDeath: string): AboutForm => {
  const metadata = (estate.estate_metadata ?? {}) as Metadata;
  return {
    first_name: estate.first_name ?? "",
    middle_name: estate.middle_name ?? "",
    last_name: estate.last_name ?? "",
    nickname: estate.nickname ?? "",
    ssn,
    date_of_birth: estate.date_of_birth ?? "",
    date_of_death: estate.date_of_death ?? "",
    citizenship: estate.citizenship ?? "",
    occupation: estate.occupation ?? "",
    employer: estate.employer ?? "",
    military: typeof metadata.military === "string" ? metadata.military : "",
    place_of_death: placeOfDeath,
  };
};

const LabeledInput: FC<{
  label: string;
  optional?: boolean;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, optional, value, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <Label>
      {label}
      {optional && <span className="font-normal text-tertiary"> (optional)</span>}
    </Label>
    <Input value={value} onChange={(event) => onChange(event.target.value)} />
  </div>
);

interface AboutSectionProps extends SectionEditProps {
  estate: EstateRow;
}

export const AboutSection: FC<AboutSectionProps> = ({
  estate,
  isEditing,
  canEdit,
  onEdit,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { user, refetch } = useAppContext();
  const { data: placeOfDeath } = usePlaceOfDeath(estate.estate_id);
  const placeOfPassing = formatPlaceOfDeath(placeOfDeath) || getStateName(estate.place_of_death);
  const { deceasedSSN } = useDeceasedSSNData();
  const { saveSSN } = useSaveSSN();
  const { ssnValue, setSsnValue, ssnVisible, setSsnVisible, ssnInputKey, remountInput } =
    useSSNField(deceasedSSN);
  const [form, setForm] = useState<AboutForm>(() => toForm(estate, ssnValue, placeOfPassing));
  const [isSaving, setIsSaving] = useState(false);
  const [placeAtEdit, setPlaceAtEdit] = useState(placeOfPassing);

  const update = (changes: Partial<AboutForm>) => setForm((prev) => ({ ...prev, ...changes }));
  const metadata = (estate.estate_metadata ?? {}) as Metadata;
  const veteran = typeof metadata.military === "string" ? YES_NO_LABELS[metadata.military] : "";
  const firstName = estate.first_name || estate.full_name?.split(" ")[0] || "";

  const handleEdit = () => {
    setForm(toForm(estate, ssnValue, placeOfPassing));
    setPlaceAtEdit(placeOfPassing);
    onEdit();
  };

  const handleCancel = () => {
    remountInput();
    onClose();
  };

  const handleDone = async () => {
    if (form.date_of_birth && form.date_of_death && form.date_of_birth > form.date_of_death) {
      enqueueSnackbar("Date of birth can't be after date of death", { variant: "error" });
      return;
    }
    if (form.ssn && !isValidSSN(form.ssn)) {
      enqueueSnackbar(SSN_VALIDATION_MESSAGE, { variant: "error" });
      return;
    }

    setIsSaving(true);
    try {
      const fullName = buildFullName(
        form.first_name.trim(),
        form.middle_name.trim(),
        form.last_name.trim(),
      );
      const current = await fetchEstateJsonFields(estate.estate_id);
      const { error } = await supabase
        .from("estates")
        .update({
          first_name: form.first_name.trim() || null,
          middle_name: form.middle_name.trim() || null,
          last_name: form.last_name.trim() || null,
          full_name: fullName || null,
          nickname: form.nickname.trim() || null,
          date_of_birth: form.date_of_birth || null,
          date_of_death: form.date_of_death || null,
          citizenship: form.citizenship || null,
          occupation: form.occupation.trim() || null,
          employer: form.employer.trim() || null,
          estate_metadata: {
            ...((current.estate_metadata ?? {}) as Metadata),
            military: form.military || null,
          },
        })
        .eq("estate_id", estate.estate_id);
      if (error) throw error;

      if (form.ssn && form.ssn !== ssnValue) {
        await saveSSN({ ssn: cleanSSN(form.ssn), estateId: estate.estate_id });
        setSsnValue(form.ssn);
      }

      const place = form.place_of_death.trim();
      if (place && place !== placeAtEdit) {
        const result = await resolveAddressLink({
          raw: place,
          purpose: "place_of_death",
          entity: { estate_id: estate.estate_id },
        });
        await queryClient.invalidateQueries({ queryKey: placeOfDeathKey(estate.estate_id) });
        if (!result?.addressId) {
          enqueueSnackbar("Couldn't save the place of passing. Please try again.", {
            variant: "error",
          });
        }
      }

      if (user?.user_id) {
        logActivity({
          estate_id: estate.estate_id,
          action_type: "updated_deceased_profile",
          user_id: user.user_id,
          entity_type: "estate",
          entity_id: String(estate.estate_id),
          metadata: {
            full_name: fullName || null,
            date_of_birth: form.date_of_birth || null,
            date_of_death: form.date_of_death || null,
            nickname: form.nickname.trim() || null,
          },
        }).catch(() => {});
      }

      refetch();
      enqueueSnackbar(TOAST.CHANGES_SAVED, { variant: "success" });
      onClose();
    } catch (err) {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
      sentryUtils.captureError(err, "Summary about save failed", { estateId: estate.estate_id });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SummaryCard
      title={firstName ? `About ${firstName}` : "About"}
      edit={{
        isEditing,
        canEdit,
        isSaving,
        onEdit: handleEdit,
        onCancel: handleCancel,
        onDone: handleDone,
      }}
    >
      <SummaryRow label="Full legal name">
        {isEditing ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <LabeledInput
                label="First name"
                value={form.first_name}
                onChange={(first_name) => update({ first_name })}
              />
              <LabeledInput
                label="Middle name"
                optional
                value={form.middle_name}
                onChange={(middle_name) => update({ middle_name })}
              />
            </div>
            <LabeledInput
              label="Last name"
              value={form.last_name}
              onChange={(last_name) => update({ last_name })}
            />
          </div>
        ) : (
          estate.full_name
        )}
      </SummaryRow>
      <SummaryRow label="Also known as">
        {isEditing ? (
          <Input
            value={form.nickname}
            onChange={(event) => update({ nickname: event.target.value })}
          />
        ) : (
          estate.nickname
        )}
      </SummaryRow>
      <SummaryRow label="Social Security Number" tooltip={WHY_WE_ASK.decedentSsn}>
        {(isEditing || ssnValue) && (
          <SSNField
            isEditing={isEditing}
            valueFormatted={form.ssn}
            onChangeFormatted={(ssn) => update({ ssn })}
            savedValue={ssnValue}
            ssnVisible={ssnVisible}
            toggleVisible={() => setSsnVisible(!ssnVisible)}
            inputKey={ssnInputKey}
          />
        )}
      </SummaryRow>
      <SummaryRow label="Date of birth">
        {isEditing ? (
          <DatePicker
            dateString={form.date_of_birth || undefined}
            onChange={() => {}}
            onDateStringChange={(value) => update({ date_of_birth: value || "" })}
            placeholder="MM/DD/YYYY"
            isBirthDate
          />
        ) : (
          formatShortDate(estate.date_of_birth)
        )}
      </SummaryRow>
      <SummaryRow label="Date of passing">
        {isEditing ? (
          <DatePicker
            dateString={form.date_of_death || undefined}
            onChange={() => {}}
            onDateStringChange={(value) => update({ date_of_death: value || "" })}
            placeholder="MM/DD/YYYY"
          />
        ) : (
          formatShortDate(estate.date_of_death)
        )}
      </SummaryRow>
      <SummaryRow label="Place of passing">
        {isEditing ? (
          <AddressInput
            value={form.place_of_death}
            onChange={(place_of_death) => update({ place_of_death })}
            placeTypes={["(cities)"]}
            country={null}
            placeholder="Search for a city"
          />
        ) : (
          placeOfPassing
        )}
      </SummaryRow>
      <SummaryRow label="Citizenship">
        {isEditing ? (
          <Combobox
            options={COUNTRY_OPTIONS}
            value={form.citizenship}
            onValueChange={(citizenship) => update({ citizenship })}
            placeholder="Select a country"
            searchPlaceholder="Search countries"
            emptyText="No country found"
          />
        ) : (
          estate.citizenship && getCountryName(estate.citizenship)
        )}
      </SummaryRow>
      <SummaryRow label="Occupation">
        {isEditing ? (
          <div className="flex flex-col gap-4">
            <LabeledInput
              label="Occupation"
              value={form.occupation}
              onChange={(occupation) => update({ occupation })}
            />
            <LabeledInput
              label="Employer"
              value={form.employer}
              onChange={(employer) => update({ employer })}
            />
          </div>
        ) : (
          [estate.occupation, estate.employer].filter(Boolean).join(", ")
        )}
      </SummaryRow>
      <SummaryRow label="Veteran">
        {isEditing ? (
          <Select value={form.military} onValueChange={(military) => update({ military })}>
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(YES_NO_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          veteran
        )}
      </SummaryRow>
    </SummaryCard>
  );
};
